import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

type Ics204SignDialogProps = {
  open: boolean;
  role: string;
  title: string;
  description?: string;
  confirmLabel?: string;
  defaultName?: string;
  /**
   * Collect the signer's position title alongside their name, for forms whose
   * signature box prints one. Opt-in so the forms that only print a name keep
   * the shorter dialog.
   */
  collectPositionTitle?: boolean;
  defaultPositionTitle?: string;
  /**
   * Collect a Comments field for boxes that print one (213RR box 7).
   */
  collectComments?: boolean;
  defaultComments?: string;
  onClose: () => void;
  onConfirm: (name: string, positionTitle?: string, comments?: string) => void;
  isPending?: boolean;
};

const Ics204SignDialog = ({
  open,
  role,
  title,
  description,
  confirmLabel = 'Sign',
  defaultName = '',
  collectPositionTitle = false,
  defaultPositionTitle = '',
  collectComments = false,
  defaultComments = '',
  onClose,
  onConfirm,
  isPending = false,
}: Ics204SignDialogProps) => {
  const [name, setName] = useState(defaultName);
  const [positionTitle, setPositionTitle] = useState(defaultPositionTitle);
  const [comments, setComments] = useState(defaultComments);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setPositionTitle(defaultPositionTitle);
      setComments(defaultComments);
    }
  }, [open, defaultName, defaultPositionTitle, defaultComments, role]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="dark tw:max-w-md"
        data-testid="ics204-sign-dialog"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description ?? `Sign as ${role}.`}
          </DialogDescription>
        </DialogHeader>
        <div className="tw:grid tw:gap-2">
          <Label htmlFor="ics204-sign-name">Name</Label>
          <Input
            id="ics204-sign-name"
            data-testid="ics204-sign-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          {collectPositionTitle ? (
            <>
              <Label htmlFor="ics204-sign-position">Position / Title</Label>
              <Input
                id="ics204-sign-position"
                data-testid="ics204-sign-position"
                value={positionTitle}
                onChange={(e) => setPositionTitle(e.target.value)}
              />
            </>
          ) : null}
          {collectComments ? (
            <>
              <Label htmlFor="ics204-sign-comments">Comments</Label>
              <Textarea
                id="ics204-sign-comments"
                data-testid="ics204-sign-comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
              />
            </>
          ) : null}
          <p className="tw:text-xs tw:text-muted-foreground">Role: {role}</p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="ics204-sign-confirm"
            disabled={!name.trim() || isPending}
            onClick={() =>
              onConfirm(
                name.trim(),
                collectPositionTitle
                  ? positionTitle.trim() || undefined
                  : undefined,
                collectComments ? comments.trim() : undefined
              )
            }
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Ics204SignDialog;
